import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import {
  CalendarItem,
  CalendarService,
  CalendarItemType,
  AdminCalendarDate,
} from '../../../services/calendar/calendar.service';
import { CalendarConfigModule } from '../../../calendar-config';
import { DateFnsModule } from 'ngx-date-fns';
import {
  CalendarEvent,
  CalendarMonthViewDay,
  CalendarView,
} from 'angular-calendar';
import { firstValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { CreateCalendarItemDialogComponent } from '../../molecules/create-calendar-item-dialog/create-calendar-item-dialog.component';
import {
  endOfISOWeek,
  endOfMonth,
  formatDate,
  formatISO,
  isSameDay,
  startOfISOWeek,
  startOfMonth,
} from 'date-fns';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ConfirmationDialogComponent } from '../../molecules/confirmation-dialog/confirmation-dialog.component';
import { MainHeadingComponent } from '../../atoms/main-heading/main-heading.component';
import { PublicHolidayDisplayNamePipe } from '../../../pipes/PublicHolidayDisplayName.pipe';

@Component({
  selector: 'app-manage-calendar-items',
  standalone: true,
  templateUrl: './manage-calendar-items.component.html',
  styleUrls: ['./manage-calendar-items.component.css'],
  imports: [
    CommonModule,
    CalendarConfigModule,
    DateFnsModule,
    MatButtonModule,
    MatIconModule,
    MainHeadingComponent,
  ],
  providers: [PublicHolidayDisplayNamePipe],
})
export class ManageCalendarItemsComponent implements OnInit {
  dates: CalendarItem[] = [];
  calendarEvents: CalendarEvent<CalendarItem>[] = [];
  adminDates: AdminCalendarDate[] = [];
  viewDate = new Date();
  view: CalendarView = CalendarView.Month;
  readonly dialog = inject(MatDialog);
  CalendarItemType = CalendarItemType;
  constructor(
    private calendarService: CalendarService,
    private publicHolidayDisplayNamePipe: PublicHolidayDisplayNamePipe,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadAdminCalendar();
  }
  async viewDateChanged(date: Date) {
    await this.loadAdminCalendar();
  }
  async loadAdminCalendar() {
    const fromDate = startOfISOWeek(startOfMonth(this.viewDate));
    const toDate = endOfISOWeek(endOfMonth(this.viewDate));
    this.adminDates = await firstValueFrom(
      this.calendarService.getAdminCalendar(fromDate, toDate)
    );
    this.dates = await firstValueFrom(this.calendarService.getAllItems());
    this.calendarEvents = this.dates.map((date) => {
      return {
        id: date.id,
        title: date.description,
        start: new Date(`${date.startDate} 00:00:00`),
        end: new Date(`${date.endDate} 23:59:59`),
        allDay: true,
        meta: date,
        cssClass: `calendar-item-${date.type}`,
      };
    });

    this.adminDates.forEach((adminDate) => {
      if (adminDate.workday.reason == 'ImplicitPublicHoliday') {
        this.calendarEvents.push({
          id: `holiday-${adminDate.date}`,
          title: adminDate.workday.publicHoliday
            ? this.publicHolidayDisplayNamePipe.transform(
                adminDate.workday.publicHoliday
              )
            : '',
          start: adminDate.date,
          end: adminDate.date,
          allDay: true,
          cssClass: `calendar-item-${CalendarItemType.HqNonWorkingDayImplicit}`,
          meta: {
            type: CalendarItemType.HqNonWorkingDayImplicit,
            startDate: '',
            endDate: '',
            scope: '',
            title: '',
            description: '',
          },
        });
      }
    });

    this.cd.markForCheck();
  }

  isWorkingDay(day: CalendarMonthViewDay): boolean {
    const adminDate = this.adminDates.find((d) => isSameDay(d.date, day.date));
    if (!adminDate) {
      return true;
    }
    return adminDate.workday.isWorkingDay;
  }

  handleDayClicked(day: CalendarMonthViewDay): void {
    // Only allow future dates to be selected
    if (day.isFuture) {
      const dialogRef = this.dialog.open(CreateCalendarItemDialogComponent, {
        width: '500px',
        maxWidth: '100vw',
        data: { date: day.date },
      });
      dialogRef
        .afterClosed()
        .subscribe(
          async (result: {
            type: CalendarItemType;
            date: Date;
            title?: string;
            description?: string;
          }) => {
            if (result) {
              // Create
              await firstValueFrom(
                this.calendarService.createItem({
                  type: result.type,
                  startDate: formatISO(result.date, { representation: 'date' }),
                  endDate: formatISO(result.date, { representation: 'date' }),
                  title: '',
                  description: result.description || '',
                  scope: '*',
                })
              );
              this.loadAdminCalendar();
            }
          }
        );
    }
  }
  handleBlackoutDateDeleteClicked(item: CalendarItem) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      maxWidth: '100vw',
      data: {
        title: 'Slet',
        prompt: `Er du sikker på at du vil slette ${
          item.type == CalendarItemType.Comment ? 'kommentaren' : 'låsen'
        } for ${formatDate(item.startDate, 'dd.MM.yyyy')}?`,
      },
    });
    dialogRef
      .afterClosed()
      .subscribe(async (result: { confirmed: boolean }) => {
        if (result && result.confirmed && item.id) {
          await firstValueFrom(this.calendarService.deleteItem(item.id));
          await this.loadAdminCalendar();
        }
      });
  }
}
